import * as React from 'react';
import { useState, useCallback, useEffect, useMemo, useRef } from 'react';

/** Material UI Packages. */
import { Box } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { randomId } from '@mui/x-data-grid-generator';

/** Material UI Icons. */
import AddIcon from '@mui/icons-material/Add';

/** Redux Packages. */
import { useDispatch, useSelector } from 'react-redux';

/** Redux Reducers. */
import { setSnackbar } from 'shared/store/slices/snackBarSlice';

/** Internal Components */
import SingleDatagrid from 'shared/ui/Datagrid';
import ThemeButton from 'shared/ui/Button';
import UpdateTabs from 'shared/ui/Tabs';
import TabPanel from 'shared/ui/TabPanel';
import { CustomTextPopover } from 'shared/ui/Popover';
import useUserRole from 'shared/hooks/useUserRole';
import useInvalidOptionWarning from 'shared/hooks/useInvalidOptionWarning';
import useFormatMismatchWarning from 'shared/hooks/useFormatMismatchWarning';
import {
    createSocketData,
    deleteSocketData,
    updateSocketData,
    handleProcessRowUpdate,
    readSocketData,
} from 'shared/lib/fetchSocket';
import { createFillDownHandler } from 'shared/lib/datagridHelpers';
import { useShipmentTodos } from '../../lib/useShipmentTodos';

import LinkDialog from './LinkDialog';
import UpdateSplit from './UpdateSplit';
import { columnConfigMap, notAllowNullKey, dateField } from 'data/shipmentDetailConfig';

/** Initial values for trade partner autocomplete options. */
const initialState = {
    il_trade_partner_id_shipper: { inputValue: '', options: [] },
    il_trade_partner_id_consignee: { inputValue: '', options: [] },
    il_trade_partner_id: { inputValue: '', options: [] },
};

const UpdateHouse = (props) => {
    /** Get props from ShipmentDetail.js */
    const { mainRow, presetData, tabConfig, hblTabValue, setHblTabValue } = props;

    /** Get the dispatch function from Redux to send actions to the store. */
    const dispatch = useDispatch();

    /** Get the current theme for styling */
    const theme = useTheme();

    /** Get userRole data from auth0. */
    const userRole = useUserRole();

    /** Value for shipment type(OIM/OEX/AIM/AEX/Trucking = 0~4). */
    const tabValue = useSelector((state) => state.tabValueForm);
    /** All history list of value changes. */
    const historyData = useSelector((state) => state.historyList);
    /** All HBL (or HAWB) rows related to the main shipment */
    const hblData = useSelector((state) => state[tabConfig.houseData]);
    /** Filtered and sorted HBL/HAWB list associated with the current mainRow */
    const hblShipmentData = useMemo(() => {
        return hblData
            .filter((item) => item[tabConfig.keyName[0]] === mainRow.id)
            .slice()
            .sort((a, b) => a.id - b.id);
    }, [hblData, mainRow, tabConfig]);
    /** Container data list for this shipment */
    const containerData = useSelector((state) => state[tabConfig.containerData]);
    /** To-do list for the currently selected shipment. */
    const todoList = useShipmentTodos(tabValue, mainRow.id);
    /** Note value of the preset applied to this shipment. */
    const presetNote = useSelector((state) => state[tabConfig.presetData]);

    /** State to track which tab is currently being edited. */
    const [editIndex, setEditIndex] = useState(-1);
    /** State for temporarily storing tab title values for each id. */
    const [tabTitles, setTabTitles] = useState({});
    /** State to manage whether there are changes in the tab title (true/false) to determine the actual value of the component. */
    const [isChange, setIsChange] = useState(false);
    /** State to manage an autocomplete options useing websocket(Volume Unit). */
    const [unitOptions, setUnitOptions] = useState([]);
    /** State to manage an autocomplete options useing websocket. */
    const [tradePartnerOptions, setTradePartnerOptions] = useState(initialState);
    /** State to manage link dialog. */
    const [linkOpen, setLinkOpen] = useState(false);
    /** State for a selected single container data when opening the link dialog. */
    const [cntrRow, setCntrRow] = useState({});
    /** Preset memo notes related to the selected preset */
    const [currentPresetNote, setCurrentPresetNote] = useState([]);
    /** State to display the position of the memo popover. */
    const [popoverState, setPopoverState] = useState({
        anchorEls: null, // key: item.id, value: anchorEl
        openIds: null, // key: item.id, value: true/false
    });

    /** Check if case_closure is Complete */
    const isCaseClosureComplete = mainRow?.case_closure === 'Complete';

    /** Helper to make all columns non-editable when case is closed. */
    const disableColumnsIfComplete = (cols) => {
        if (!isCaseClosureComplete) return cols;
        return cols.map((col) => ({ ...col, editable: false }));
    };

    /** Column definitions. */
    const hblCols = columnConfigMap[tabConfig.columns[0]] || [];
    const hblCols2 = columnConfigMap[tabConfig.columns[1]] || [];
    const cntrCols = columnConfigMap[tabConfig.columns[2]] || [];

    /** When mainRow or preset data changes, update the currently selected preset notes. */
    useEffect(() => {
        const presetId = mainRow?.il_opus_reference_preset?.il_preset_id;
        if (presetId && Array.isArray(presetNote)) {
            const note = presetNote.find((item) => item.id === presetId)?.il_preset_table_note ?? [];
            setCurrentPresetNote(note);
        }
    }, [presetNote, mainRow]);

    /** Handle HBL/HAWB tab selection change */
    const handleChangeTab = (_, newValue) => setHblTabValue(newValue);

    /** Add a new HBL/HAWB entry row via WebSocket */
    const handleAddTab = () => {
        if (isCaseClosureComplete) return;
        const newHBL = { ...tabConfig.newHBL, id: randomId(), [tabConfig.keyName[0]]: mainRow.id };
        createSocketData(dispatch, tabValue, 'row', tabConfig.tableName[1], tabConfig.addReducer[1], newHBL);
    };

    /**
     * Handle HBL tab deletion.
     * Ensures at least one tab remains and reorders the selected tab index.
     */
    const handleDeleteTab = (tab) => {
        if (isCaseClosureComplete) return;
        if (hblShipmentData.length > 1) {
            if (window.confirm('Are you sure you want to delete the entry?')) {
                let oldTabValue = hblTabValue;
                deleteSocketData(
                    dispatch,
                    tabValue,
                    'row',
                    tabConfig.tableName[1],
                    tabConfig.deleteReducer[1],
                    tab.id,
                    () => {
                        if (oldTabValue !== 0) setHblTabValue(oldTabValue - 1);
                    }
                );
            }
        } else {
            window.alert('At least one HBL must be provided. ');
        }
    };

    /** Handle to change the title of a tab. */
    const handleChangeTabTitle = (index, id) => (event) => {
        const newTitle = event.target.value;
        setTabTitles((prev) => ({ ...prev, [id]: newTitle }));
        setIsChange(true);
    };

    /** Handles double-clicking a tab to enable title editing. */
    const handleDoubleClick = (index) => {
        if (isCaseClosureComplete) return;
        setEditIndex(index);
    };

    /**
     * Handler that checks for changes and updates the state via emit when focus is lost.
     *
     * @param {string} id - The unique identifier associated with the component.
     * @param {object} originalValue - The original value of the component.
     */
    const handleBlur = (id, originalValue) => {
        if (isCaseClosureComplete) return;
        const rawTitle = tabTitles[id];
        const newTitle = rawTitle?.trim().toUpperCase();
        const keyName = tabValue === 0 || tabValue === 1 ? 'hbl' : 'hawb';

        const onSuccess = () => {
            setEditIndex(-1);
            setIsChange(false);
        };

        if (!newTitle || newTitle === originalValue) {
            onSuccess();
            return;
        }
        const previousValue = rawTitle;
        const onFailure = () => setTabTitles((prev) => ({ ...prev, [id]: previousValue }));
        const sendValue = { id: id, key: keyName, value: newTitle, table_id: [mainRow.id] };

        updateSocketData(
            dispatch,
            tabValue,
            'cell',
            tabConfig.tableName[1],
            tabConfig.updateReducer[1],
            sendValue,
            onSuccess,
            onFailure
        );
    };

    /** Calls handleBlur when 'Enter'key is pressed, causing the component to lose focus and send the data to server. */
    const handleKeyDown = (event, id, originalValue) => {
        if (event.key === 'Enter') {
            event.preventDefault();
            handleBlur(id, originalValue);
        }
    };

    /** Handler for emitting to the server to add a new container row. */
    const handleAddContainer = (newValue) => {
        if (isCaseClosureComplete) return;
        const newCNTR = { ...tabConfig.newCNTR, id: randomId(), [tabConfig.keyName[1]]: newValue.id };
        createSocketData(dispatch, tabValue, 'row', tabConfig.tableName[2], tabConfig.addReducer[2], newCNTR);
    };

    /**
     * Handle to delete a container row.
     * Ensure at least one container remains before delete.
     *
     * @param {string} id - The ID of the container to delete.
     */
    const handleDeleteClick = (id) => {
        if (isCaseClosureComplete) return;
        const hbl_id = hblShipmentData.map((item) => item.id);
        const matchedCNTR = containerData.filter((item) => hbl_id.includes(item[tabConfig.keyName[1]]));

        if (matchedCNTR.length > 1) {
            if (window.confirm('Are you sure you want to delete the entry?')) {
                deleteSocketData(dispatch, tabValue, 'row', tabConfig.tableName[2], tabConfig.deleteReducer[2], id);
            }
        } else {
            window.alert('At least one container must be provided. ');
        }
    };

    /** Handler for updating a selected contaiiner data and link dialog. */
    const handleLinkClick = (row) => {
        setCntrRow(row);
        setLinkOpen(true);
    };

    /** Handle to change autocomplete opus unit list and call Websocket to fetch the searched result. */
    const handleUnitChange = (event, value) => {
        readSocketData(dispatch, tabValue, 'cell', 'il_opus_volume_unit', '', value, setUnitOptions);
    };

    /** Handle to change autocomplete trucker name and call Websocket to fetch the searched result. */
    const handleTradePartnerChange = (event, value, params) => {
        const type = params.field;
        const onSuccess = (receivedData) => {
            setTradePartnerOptions((prev) => ({
                ...prev,
                [type]: { ...prev[type], options: receivedData },
            }));
        };
        readSocketData(dispatch, tabValue, 'cell', 'il_trade_partner', type, value, onSuccess);
    };

    /** Prefetch unit/trucker options as soon as the cell enters edit mode, before the user types. */
    const handleCellEditStart = (params) => {
        if (params.field === 'il_opus_volume_unit_id') {
            handleUnitChange(null, params.value || '');
        } else if (params.field === 'il_trade_partner_id') {
            handleTradePartnerChange(null, params.value?.name ?? '', params);
        }
    };

    /** Warn the user when a pasted value is not one of the allowed select options. */
    const handleInvalidOption = useInvalidOptionWarning();
    /** Warn the user when a pasted Shipper/Consignee isn't a copied partner object. */
    const handleFormatMismatch = useFormatMismatchWarning();

    /** Handle to set the anchor element for the memo popover. */
    const handleMemoClick = (event, id) => setPopoverState({ openIds: id, anchorEls: event.currentTarget });

    /** Handle to close a memo popover. */
    const handleMemoClose = () => setPopoverState({ openIds: null, anchorEls: null });

    /** Determine the height of the detail panel dynamically. */
    const getDetailPanelHeight = useCallback(() => 'auto', []);

    /** Provides additional details about a contianer alias row. */
    const getDetailPanelContent = useCallback(
        ({ row }) => <UpdateSplit mainRow={mainRow} containerRow={row} tabConfig={tabConfig} />,
        [mainRow]
    );

    /**
     * Container rows whose Split panel should be shown expanded, because they already have alias data.
     * `initialState` only seeds this once at mount, so it never reflects a container gaining its first
     * alias afterward; track it as controlled state and merge in newly-aliased ids as containerData updates.
     */
    const [splitExpandedRowIds, setSplitExpandedRowIds] = useState(() =>
        containerData.filter((row) => row.has_alias).map((row) => row.id)
    );
    const previousHasAliasIdsRef = useRef(new Set(splitExpandedRowIds));

    useEffect(() => {
        const currentHasAliasIds = containerData.filter((row) => row.has_alias).map((row) => row.id);
        const newlyAliased = currentHasAliasIds.filter((id) => !previousHasAliasIdsRef.current.has(id));
        if (newlyAliased.length > 0) {
            setSplitExpandedRowIds((prev) => [...new Set([...prev, ...newlyAliased])]);
        }
        previousHasAliasIdsRef.current = new Set(currentHasAliasIds);
    }, [containerData]);

    const handleSplitExpandedRowIdsChange = useCallback((newIds) => setSplitExpandedRowIds(newIds), []);

    return (
        <>
            <UpdateTabs
                value={hblTabValue}
                label={tabValue === 0 || tabValue === 1 ? 'hbl' : 'hawb'}
                disableAdd={isCaseClosureComplete}
                tabsData={hblShipmentData}
                tabTitles={tabTitles}
                editIndex={editIndex}
                isChange={isChange}
                handleChange={handleChangeTab}
                handleDoubleClick={handleDoubleClick}
                handleChangeTabTitle={handleChangeTabTitle}
                handleKeyDown={handleKeyDown}
                handleBlur={handleBlur}
                handleDeleteTab={handleDeleteTab}
                handleAddTab={handleAddTab}
            />

            {presetData &&
                hblShipmentData.map((tab, index) => {
                    const cntrData = containerData.filter((item) => item[tabConfig.keyName[1]] === tab.id);
                    const matchedTodo = todoList?.filter((item) => item.tablename === tabConfig.tableName[1]);
                    const matchedTodoContainer = todoList?.filter((item) => item.tablename === tabConfig.tableName[2]);

                    /** Copy the first container row's value in a column to every row below it. */
                    const handleContainerFillDown = createFillDownHandler({
                        dispatch,
                        disabled: isCaseClosureComplete,
                        rows: cntrData,
                        tabValue,
                        methodTableName: tabConfig.tableName[2],
                        reducerName: tabConfig.updateReducer[2],
                        getTableId: (row) => [mainRow.id, row[tabConfig.keyName[1]]],
                        formData: containerData,
                        notAllowNullKey,
                        dateField,
                    });
                    return (
                        <TabPanel key={index} value={hblTabValue} index={index} dir={theme.direction}>
                            <Box sx={{ display: 'flex', flexDirection: 'column', pt: 0.5, rowGap: 1 }}>
                                <SingleDatagrid
                                    rows={[tab]}
                                    cols={disableColumnsIfComplete(
                                        hblCols(
                                            userRole,
                                            presetData,
                                            tab,
                                            matchedTodo,
                                            unitOptions,
                                            handleUnitChange,
                                            tradePartnerOptions,
                                            handleTradePartnerChange,
                                            handleFormatMismatch
                                        )
                                    )}
                                    columnGroupingModel={columnConfigMap[tabConfig.columns[3]](
                                        presetData,
                                        tabValue,
                                        historyData,
                                        tab
                                    )}
                                    maxHeight={'95px'}
                                    processRowUpdate={(newRow) => {
                                        if (isCaseClosureComplete)
                                            return Promise.resolve(
                                                hblShipmentData.find((r) => r.id === newRow.id) || newRow
                                            );
                                        return handleProcessRowUpdate({
                                            dispatch,
                                            tabValue,
                                            methodTableName: tabConfig.tableName[1],
                                            reducerName: tabConfig.updateReducer[1],
                                            tableId: [mainRow.id],
                                            formData: hblShipmentData,
                                            newRow,
                                            notAllowNullKey,
                                            dateField,
                                        });
                                    }}
                                    additional={{ onCellEditStart: handleCellEditStart }}
                                />
                                {tabConfig.hasSecondTable && (
                                    <SingleDatagrid
                                        rows={[tab]}
                                        cols={disableColumnsIfComplete(
                                            hblCols2(
                                                userRole,
                                                presetData,
                                                tab,
                                                matchedTodo,
                                                unitOptions,
                                                handleUnitChange,
                                                tradePartnerOptions,
                                                handleTradePartnerChange
                                            )
                                        )}
                                        columnGroupingModel={columnConfigMap[tabConfig.columns[4]](
                                            presetData,
                                            tabValue,
                                            historyData,
                                            tab
                                        )}
                                        maxHeight={'95px'}
                                        processRowUpdate={(newRow) => {
                                            if (isCaseClosureComplete)
                                                return Promise.resolve(
                                                    hblShipmentData.find((r) => r.id === newRow.id) || newRow
                                                );
                                            return handleProcessRowUpdate({
                                                dispatch,
                                                tabValue,
                                                methodTableName: tabConfig.tableName[1],
                                                reducerName: tabConfig.updateReducer[1],
                                                tableId: [mainRow.id],
                                                formData: hblShipmentData,
                                                newRow,
                                                notAllowNullKey,
                                                dateField,
                                            });
                                        }}
                                        additional={{ onCellEditStart: handleCellEditStart }}
                                    />
                                )}

                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 0.5,
                                        justifyContent: 'flex-end',
                                        width: '100%',
                                        pt: 1,
                                    }}
                                >
                                    {tabConfig.preset.map((item, idx) => {
                                        const matchedPreset = currentPresetNote?.find(
                                            (el) => el.attribute === item.attribute
                                        );
                                        return (
                                            <CustomTextPopover
                                                key={item.id}
                                                open={popoverState.openIds === item.id}
                                                anchorEl={
                                                    popoverState.openIds === item.id ? popoverState.anchorEls : null
                                                }
                                                handleClick={(e) => handleMemoClick(e, item.id)}
                                                handleClose={handleMemoClose}
                                                content={matchedPreset ? matchedPreset.value : null}
                                                title={item.label}
                                                buttonStyle={{ bgcolor: matchedPreset ? '#FFC300' : '#dfdfdf' }}
                                            />
                                        );
                                    })}

                                    <ThemeButton
                                        text={'NEW'}
                                        icon={<AddIcon />}
                                        sx={{ width: '68px', alignSelf: 'flex-end' }}
                                        onClick={() => handleAddContainer(tab)}
                                        disabled={isCaseClosureComplete}
                                    />
                                </Box>
                                <Box>
                                    <SingleDatagrid
                                        rows={cntrData}
                                        cols={disableColumnsIfComplete(
                                            cntrCols(
                                                handleDeleteClick,
                                                presetData,
                                                unitOptions,
                                                handleUnitChange,
                                                tradePartnerOptions,
                                                handleTradePartnerChange,
                                                matchedTodoContainer,
                                                handleLinkClick,
                                                mainRow,
                                                handleContainerFillDown,
                                                handleInvalidOption
                                            )
                                        )}
                                        cellSelection={true}
                                        columnGroupingModel={columnConfigMap[tabConfig.columns[5]](
                                            presetData,
                                            tabValue,
                                            historyData,
                                            cntrData
                                        )}
                                        sx={{
                                            height: tabConfig.height,
                                            minHeight: tabConfig.minHeight,
                                        }}
                                        processRowUpdate={(newRow) => {
                                            if (isCaseClosureComplete)
                                                return Promise.resolve(
                                                    containerData.find((r) => r.id === newRow.id) || newRow
                                                );
                                            return handleProcessRowUpdate({
                                                dispatch,
                                                tabValue,
                                                methodTableName: tabConfig.tableName[2],
                                                reducerName: tabConfig.updateReducer[2],
                                                tableId: [mainRow.id, newRow[tabConfig.keyName[1]]],
                                                formData: containerData,
                                                newRow,
                                                notAllowNullKey,
                                                dateField,
                                            });
                                        }}
                                        onProcessRowUpdateError={(error) => {
                                            dispatch(
                                                setSnackbar({
                                                    open: true,
                                                    message: error.message,
                                                    severity: 'error',
                                                })
                                            );
                                        }}
                                        additional={{ onCellEditStart: handleCellEditStart }}
                                        {...(tabConfig.hasSplit
                                            ? {
                                                  getDetailPanelHeight: getDetailPanelHeight,
                                                  getDetailPanelContent: getDetailPanelContent,
                                                  detailPanelExpandedRowIds: splitExpandedRowIds.filter((id) =>
                                                      cntrData.some((row) => row.id === id)
                                                  ),
                                                  onDetailPanelExpandedRowIdsChange: handleSplitExpandedRowIdsChange,
                                              }
                                            : {})}
                                    />
                                </Box>
                                {tabValue === 0 || tabValue === 2 || tabValue === 4 ? (
                                    <LinkDialog
                                        linkOpen={linkOpen}
                                        setLinkOpen={setLinkOpen}
                                        cntrRow={cntrRow}
                                        cntrData={cntrData}
                                        presetData={presetData}
                                        matchedTodoContainer={matchedTodoContainer}
                                        containerData={containerData}
                                        tabConfig={tabConfig}
                                        mainRow={mainRow}
                                    />
                                ) : (
                                    <></>
                                )}
                            </Box>
                        </TabPanel>
                    );
                })}
        </>
    );
};

export default React.memo(UpdateHouse);
